import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import {
  LinkedItemPayload,
  LinkedItemsPayload,
} from '../../types/payloads/linked-items.payloads';
import {
  LinkedItemAPIResponse,
  LinkedItemParentTable,
  LinkedItemParentTableType,
  LinkedItemSearchItemAPIResponse,
  LinkedItemSearchOption,
} from '../../types/responses/linked-items.responses';

@Injectable({
  providedIn: 'root',
})
export class LinkedItemsService {
  constructor(private apiService: APICoreService) {}

  searchItems(
    search: string,
    exclude_table: string,
    exclude_id: number
  ): Observable<LinkedItemSearchOption[]> {
    return this.apiService
      .getRequest('growelab/crosslink/search-items', {
        search,
        exclude_table,
        exclude_id,
      })
      .pipe(
        map((response) => LinkedItemsService.mapSearchItems(response.items))
      );
  }

  getLinkedItems(
    target_table: string,
    target_id: number
  ): Observable<{ items: LinkedItemAPIResponse[] }> {
    return this.apiService.getRequest('growelab/crosslink', {
      target_table,
      target_id,
    });
  }

  updateLinkedItem(payload: LinkedItemPayload): Observable<LinkedItemPayload> {
    return this.apiService.postRequest('growelab/crosslink', payload);
  }

  deleteLinkedItem(id: number) {
    return this.apiService.deleteRequest(`growelab/crosslink/${id}`);
  }

  linkItems(payload: LinkedItemsPayload) {
    const normalizedPayload: LinkedItemsPayload = {
      ...payload,
      items: payload.items.map((item) => ({
        ...item,
        parent_table_type:
          item.parent_table === LinkedItemParentTable.IMPLEMENTATION_PLAN
            ? null
            : LinkedItemsService.normalizeParentTableType(
                item.parent_table_type
              ),
      })),
    };

    return this.apiService.postRequest(
      'growelab/crosslink/link-items',
      normalizedPayload
    );
  }

  private static mapSearchItems(
    items: LinkedItemSearchItemAPIResponse[]
  ): LinkedItemSearchOption[] {
    return items.map((item) => LinkedItemsService.mapSearchItem(item));
  }

  private static mapSearchItem(
    item: LinkedItemSearchItemAPIResponse
  ): LinkedItemSearchOption {
    const parentTable = LinkedItemsService.getParentTable(item.parent_table);
    const parentTableType = LinkedItemsService.normalizeParentTableType(
      item.parent_table_type
    );

    const linkUrl = LinkedItemsService.buildLinkUrl(
      parentTable,
      parentTableType,
      item.parent_id
    );

    const linkIcon = LinkedItemsService.getDefaultIcon(parentTable);

    return {
      parent_table: parentTable,
      parent_table_type: parentTableType,
      parent_id: item.parent_id,
      link_icon: linkIcon,
      link_title: item.title,
      link_url: linkUrl,
    };
  }

  private static buildLinkUrl(
    parentTable: LinkedItemParentTable,
    parentTableType: LinkedItemParentTableType | null,
    parentId: number
  ): string {
    if (!parentId || parentId <= 0) {
      return '';
    }

    if (parentTable === LinkedItemParentTable.COACHLOG) {
      if (
        parentTableType === LinkedItemParentTableType.UNIVERSAL_LOG ||
        parentTableType === LinkedItemParentTableType.GENERAL_LOG
      ) {
        return `/general/log/${parentId}/info`;
      }

      if (parentTableType === LinkedItemParentTableType.COACHING_CONVERSATION) {
        return `/coaching/log/${parentId}/info`;
      }

      if (
        parentTableType === LinkedItemParentTableType.CLASSROOM_VISIT ||
        parentTableType === LinkedItemParentTableType.OBSERVATION
      ) {
        return `/observation/log/${parentId}/info`;
      }
    }

    if (parentTable === LinkedItemParentTable.PD_SESSION) {
      if (parentTableType === LinkedItemParentTableType.MICROLEARNING) {
        return `/training/session/microlearning/${parentId}`;
      }
      return `/training/session/${parentId}`;
    }

    if (parentTable === LinkedItemParentTable.IMPLEMENTATION_PLAN) {
      return `/plans/implementation-plan/${parentId}/dashboard`;
    }

    return '';
  }

  private static getDefaultIcon(parentTable: LinkedItemParentTable): string {
    switch (parentTable) {
      case LinkedItemParentTable.COACHLOG:
        return 'card-checklist';
      case LinkedItemParentTable.PD_SESSION:
        return 'mortarboard';
      case LinkedItemParentTable.IMPLEMENTATION_PLAN:
        return 'compass';
      default:
        return 'link-45deg';
    }
  }

  private static getParentTable(value: string): LinkedItemParentTable {
    const normalized = value.toLowerCase();

    if (normalized.includes('egrowe_coachlog')) {
      return LinkedItemParentTable.COACHLOG;
    }

    if (normalized.includes('implementation_plan')) {
      return LinkedItemParentTable.IMPLEMENTATION_PLAN;
    }

    if (normalized.includes('pd_session')) {
      return LinkedItemParentTable.PD_SESSION;
    }

    return LinkedItemParentTable.COACHLOG;
  }

  private static normalizeParentTableType(
    parentTableType: LinkedItemParentTableType | string | null
  ): LinkedItemParentTableType | null {
    const normalizedType = String(parentTableType || '')
      .trim()
      .toLowerCase();

    if (!normalizedType || normalizedType === 'null') {
      return null;
    }

    return normalizedType as LinkedItemParentTableType;
  }
}
